import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import BackendAxios from "../../BackendAxios";
import DetailsMediaPanel from "./DetailsMediaPanel";
import DetailsFieldsPanel from "./DetailsFieldsPanel";
import DetailsInfoDrawer from "./DetailsInfoDrawer";
import {
  formatNumber,
  isSquareFootageType,
  parseSquareFootageValue,
  sortFieldValuesForDisplay,
} from "./detailsDialogUtils";

function DetailsDialog(props) {
  const { open, onClose, entry, sizing } = props;
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeIndex, setActiveIndex] = useState(0);
  const [infoOpen, setInfoOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [statusSeverity, setStatusSeverity] = useState("success");
  const phoneNavOffset = Number(sizing?.xs?.usedWidth || 0);

  const images = useMemo(
    () =>
      Array.isArray(entry?.images)
        ? entry.images.filter((image) => image?.url)
        : [],
    [entry]
  );
  const referenceImageUrl = useMemo(() => {
    const referenceImage =
      images.find(
        (image) =>
          String(image?.type || "").trim().toLowerCase() === "cover" &&
          (image?.previewUrl || image?.url)
      ) || images.find((image) => image?.previewUrl || image?.url);

    return referenceImage?.previewUrl || referenceImage?.url || "";
  }, [images]);

  const fieldValues = useMemo(
    () => sortFieldValuesForDisplay(entry?.fieldValues),
    [entry]
  );

  const totalSquareFeet = useMemo(() => {
    const totals = fieldValues.reduce(
      (acc, fieldValue) => {
        if (!isSquareFootageType(fieldValue?.fieldId?.type)) return acc;

        const parsed = parseSquareFootageValue(fieldValue?.value);
        if (parsed === null) return acc;

        return {
          value: acc.value + parsed,
          count: acc.count + 1,
        };
      },
      { value: 0, count: 0 }
    );

    return totals.count > 0 ? formatNumber(totals.value) : null;
  }, [fieldValues]);

  useEffect(() => {
    setActiveIndex(0);
    setInfoOpen(false);
    setName("");
    setPhone("");
    setEmail("");
    setMessage("");
  }, [entry?._id, open]);

  const sendInfoRequest = async () => {
    if (sending) return;

    setSending(true);
    try {
      const entryName = entry?.name || "Unknown Entry";
      const referenceHeader = `In Reference to ${entryName}`;
      await BackendAxios.post("/email/send", {
        name,
        phone,
        email,
        message: `${referenceHeader}\n\n${message}`,
        referenceImageUrl,
      });

      setName("");
      setPhone("");
      setEmail("");
      setMessage("");
      setStatusSeverity("success");
      setStatusText("Your request has been sent.");
      setStatusOpen(true);
      setInfoOpen(false);
    } catch (error) {
      setStatusSeverity("error");
      setStatusText("Failed to send request.");
      setStatusOpen(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isPhone}
      keepMounted
      PaperProps={{
        sx: {
          ...(isPhone
            ? {
                height: "100dvh",
                maxHeight: "100dvh",
                borderRadius: 0,
              }
            : {}),
        },
      }}
    >
      <DialogTitle sx={{ pr: isPhone ? `${phoneNavOffset}px` : undefined }}>
        {entry?.name || "Details"}
      </DialogTitle>
      <DialogContent dividers className="browse-designs-details-content">
        <Box
          className="browse-designs-details-content-inner"
          sx={{
            pr: isPhone ? `${phoneNavOffset}px` : 0,
            boxSizing: "border-box",
          }}
        >
          <DetailsMediaPanel
            entry={entry}
            images={images}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
          />

          <DetailsFieldsPanel
            entry={entry}
            fieldValues={fieldValues}
            totalSquareFeet={totalSquareFeet}
          />
        </Box>

        <Box
          className={`browse-designs-details-info-overlay${
            infoOpen ? " is-open" : ""
          }`}
        >
          <DetailsInfoDrawer
            open={infoOpen}
            entryName={entry?.name}
            images={images}
            name={name}
            phone={phone}
            email={email}
            message={message}
            sending={sending}
            onNameChange={setName}
            onPhoneChange={setPhone}
            onEmailChange={setEmail}
            onMessageChange={setMessage}
            onSubmit={sendInfoRequest}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          pr: isPhone ? `${phoneNavOffset}px` : undefined,
          pb: isPhone ? "calc(8px + env(safe-area-inset-bottom, 0px))" : undefined,
          flexShrink: 0,
        }}
      >
        <Box />
        <Button
          variant="contained"
          size="small"
          onClick={() => setInfoOpen((prev) => !prev)}
          sx={{ background: "#5f738f", "&:hover": { background: "#4d6079" } }}
        >
          {infoOpen ? "Hide Information Form" : "Get More Information"}
        </Button>
        <Box sx={{ justifySelf: "end" }}>
          <Button onClick={onClose}>Close</Button>
        </Box>
      </DialogActions>

      <Snackbar
        open={statusOpen}
        autoHideDuration={5000}
        onClose={() => setStatusOpen(false)}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Alert
          onClose={() => setStatusOpen(false)}
          severity={statusSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {statusText}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

export default DetailsDialog;
